/**
 * SyncFailureBanner — top-of-app warning when photos aren't syncing.
 *
 * iter 350 — replaces the deleted `/sync-monitor` dashboard. Jake's
 * words: "Maybe we should just get a message if something isn't
 * syncing right." That's this. Lives inside `Layout.jsx`, polls
 * `/api/sync-monitor/summary` every 60s, and only renders when
 * there's an actual problem to surface. Silent otherwise.
 *
 * Visibility rules (kept tight on purpose — noisy banners train
 * people to ignore them):
 *   - today_failed > 0  →  red "N photo syncs failed today" banner
 *   - pending_retries > 3  →  amber "N retries pending" banner
 *   - neither → render nothing
 *
 * Dismiss persists for the current browser session (sessionStorage)
 * so the banner doesn't nag between page navigations.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { AlertTriangle, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import BACKEND_URL from "../lib/apiConfig";

const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60_000;
const DISMISS_KEY = "sync_banner_dismissed_v1";

export default function SyncFailureBanner() {
  const [summary, setSummary] = useState(null);
  const [dismissedAt, setDismissedAt] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) || null; }
    catch { return null; }
  });
  const [retrying, setRetrying] = useState(false);

  const poll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sync-monitor/summary`, { timeout: 8000 });
      setSummary(res.data);
    } catch {
      // Silent — if the endpoint is down, we don't want to surface
      // *this* failure via a banner that's itself failing.
    }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(() => { if (!document.hidden) poll(); }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [poll]);

  const handleDismiss = () => {
    // Stamp the dismissal with the current problem signature so a new
    // failure later re-shows the banner.
    const sig = `${summary?.today_failed || 0}:${summary?.pending_retries || 0}`;
    try { sessionStorage.setItem(DISMISS_KEY, sig); } catch { /* private mode */ }
    setDismissedAt(sig);
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const res = await axios.post(`${API}/sync-monitor/retry-all`);
      toast.success(`Queued ${res.data?.queued || res.data?.retried || "all"} retries`);
      await poll();
    } catch {
      toast.error("Retry failed — try again in a minute");
    } finally {
      setRetrying(false);
    }
  };

  if (!summary) return null;
  const failed = summary.today_failed || 0;
  const pending = summary.pending_retries || 0;
  if (failed === 0 && pending <= 3) return null;

  // Check dismissal against the current signature. If dismissed for
  // *this exact* state, hide. If the state changed (more failures
  // appeared), re-show.
  const sig = `${failed}:${pending}`;
  if (dismissedAt === sig) return null;

  const tone = failed > 0
    ? { bg: "bg-[#FF3B30]/10", border: "border-[#FF3B30]/30", text: "text-[#FF3B30]" }
    : { bg: "bg-[#FF9500]/10", border: "border-[#FF9500]/30", text: "text-[#FF9500]" };

  const msg = failed > 0
    ? `${failed} photo ${failed === 1 ? "sync has" : "syncs have"} failed today${pending > 0 ? ` (${pending} retry pending)` : ""}.`
    : `${pending} photo ${pending === 1 ? "sync is" : "syncs are"} pending retry.`;

  return (
    <div
      className={`sticky top-0 z-40 ${tone.bg} ${tone.border} border-b px-4 py-2 flex items-center gap-3 text-sm`}
      data-testid="sync-failure-banner"
      role="alert"
    >
      <AlertTriangle className={`h-4 w-4 ${tone.text} flex-shrink-0`} />
      <span className="text-zinc-200 flex-1 truncate">
        <strong className={tone.text}>Sync issue:</strong> {msg}
      </span>
      <button
        onClick={handleRetry}
        disabled={retrying}
        className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-white/[0.08] text-zinc-200 hover:bg-white/[0.15] hover:text-white disabled:opacity-40 transition-colors`}
        data-testid="sync-failure-banner-retry"
      >
        <RefreshCw className={`h-3 w-3 ${retrying ? "animate-spin" : ""}`} /> Retry all
      </button>
      <button
        onClick={handleDismiss}
        className="text-zinc-500 hover:text-zinc-200 transition-colors flex-shrink-0"
        data-testid="sync-failure-banner-dismiss"
        title="Dismiss until next failure"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
